import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import GroupsIcon from '@mui/icons-material/Groups';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import PublicIcon from '@mui/icons-material/Public';
import {
  asArray,
  BRAND_BLUE,
  DARK_TEXT,
  DataPills,
  EmptyState,
  ErrorState,
  formatNumber,
  getInitials,
  hasMeaningfulValue,
  hasValue,
  LoadingState,
  MetricCard,
  OsintShell,
  pick,
  SectionCard,
  shellCardSx,
  SLATE_TEXT,
  SourceRefs,
  stringifyValue,
  titleCase,
  useOsintFetch,
  valueText,
} from './osintCommon';
import { BASEURL } from '../../Constants';

const getEmployeeId = (employee) => pick(employee, ['employee_id', 'employeeId', 'id'], '');
const getName = (employee) => pick(employee, ['name', 'full_name', 'fullName'], 'Unknown Person');
const getContactValue = (item) => {
  if (!hasMeaningfulValue(item)) return '';
  if (typeof item !== 'object') return String(item);
  return item.value || item.email || item.phone || item.number || item.url || item.address || valueText(item, '');
};
const getEmails = (employee) => asArray(pick(employee, ['emails', 'email'], []))
  .map(getContactValue)
  .filter(Boolean);
const getPhones = (employee) => {
  const phones = pick(employee, ['phones', 'phone_numbers', 'phone'], []);
  if (phones && typeof phones === 'object' && !Array.isArray(phones)) {
    return Object.entries(phones)
      .flatMap(([type, values]) => asArray(values).map((item) => {
        const value = getContactValue(item);
        return value ? `${value}${type ? ` (${titleCase(type)})` : ''}` : '';
      }))
      .filter(Boolean);
  }
  return asArray(phones).map(getContactValue).filter(Boolean);
};

const getCountryFlag = (countryCode) => {
  const code = String(countryCode || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '';
  return code
    .split('')
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join('');
};

const countryCodeFromLocation = (location) => {
  const country = typeof location === 'object' && location ? String(location.country || location.country_code || location.countryCode || '').trim() : '';
  if (/^[A-Za-z]{2}$/.test(country)) return country.toUpperCase();
  const text = typeof location === 'string' ? location : [location?.city, location?.state, location?.country].filter(Boolean).join(' ');
  if (/\b(united states|usa|us)\b/i.test(text)) return 'US';
  if (/\bcanada\b/i.test(text)) return 'CA';
  if (/\bunited kingdom|uk\b/i.test(text)) return 'GB';
  return '';
};

const getLocationParts = (employee) => {
  const location = pick(employee, ['location'], '');
  if (!hasMeaningfulValue(location)) {
    return { primary: 'Unknown location', secondary: 'No public location returned', flag: '', isKnown: false };
  }

  if (typeof location !== 'object') {
    const flag = getCountryFlag(countryCodeFromLocation(location));
    return { primary: String(location), secondary: '', flag, isKnown: true };
  }

  const city = [location.city, location.state || location.region].filter(Boolean).join(', ');
  const country = location.country || location.country_code || location.countryCode || '';
  const fullAddress = location.full_address || location.fullAddress || '';
  return {
    primary: city || country || fullAddress || 'Known location',
    secondary: fullAddress && fullAddress !== city ? fullAddress : country,
    flag: getCountryFlag(countryCodeFromLocation(location)),
    isKnown: true,
  };
};

const getLocation = (employee) => {
  const location = pick(employee, ['location'], '');
  if (!hasMeaningfulValue(location)) return 'Unknown';
  if (typeof location !== 'object') return String(location);
  return location.full_address || [location.city, location.state, location.country].filter(Boolean).join(', ') || 'Unknown';
};
const getSocials = (employee) => pick(employee, ['social_media', 'socialMedia', 'social_profiles', 'socialProfiles', 'socials'], {});
const getSocialUrl = (value) => {
  if (!hasMeaningfulValue(value)) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(getSocialUrl).find(Boolean) || '';
  if (typeof value === 'object') {
    return value.url || value.link || value.profile_url || value.profileUrl || value.linkedin_url || value.linkedinUrl || value.twitter_url || value.facebook_url || value.instagram_url || getSocialUrl(value.profile);
  }
  return '';
};

const normalizePlatform = (platform) => {
  const value = String(platform || '').toLowerCase();
  if (value.includes('linkedin')) return 'linkedin';
  if (value.includes('twitter') || value === 'x' || value.includes('x.com')) return 'twitter';
  if (value.includes('facebook')) return 'facebook';
  if (value.includes('instagram')) return 'instagram';
  return value || 'other';
};

const platformMeta = {
  linkedin: { label: 'LinkedIn', Icon: LinkedInIcon, accent: BRAND_BLUE },
  twitter: { label: 'Twitter/X', Icon: TwitterIcon, accent: '#0EA5E9' },
  facebook: { label: 'Facebook', Icon: FacebookIcon, accent: '#2563EB' },
  instagram: { label: 'Instagram', Icon: InstagramIcon, accent: '#C13584' },
  other: { label: 'Other', Icon: PublicIcon, accent: '#7C3AED' },
};

const getSocialLinks = (employee) => Object.entries(getSocials(employee) || {})
  .map(([platform, value]) => ({ platform: normalizePlatform(platform), originalPlatform: platform, url: getSocialUrl(value), value }))
  .filter((item) => hasMeaningfulValue(item.url) || hasMeaningfulValue(item.value?.profile) || hasMeaningfulValue(item.value?.posts));

const hasSocialPlatform = (employee, platform) => getSocialLinks(employee).some((item) => item.platform === platform);

const isExecutive = (employee) => {
  const text = [
    pick(employee, ['seniority'], ''),
    pick(employee, ['title', 'job_title', 'role'], ''),
  ].join(' ');
  return /founder|chief|ceo|cfo|coo|cto|ciso|president|vice president|\bvp\b|owner|executive/i.test(text);
};

const ContactList = ({ values, accent, emptyLabel, limit = 2 }) => {
  const visible = values.slice(0, limit);
  const remaining = Math.max(0, values.length - visible.length);
  if (!values.length) {
    return (
      <Typography variant="body2" sx={{ color: SLATE_TEXT, fontStyle: 'italic' }}>
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Stack spacing={0.6}>
      {visible.map((value) => (
        <Typography key={value} variant="body2" sx={{ color: DARK_TEXT, fontWeight: 800, wordBreak: 'break-word', lineHeight: 1.45 }}>
          {value}
        </Typography>
      ))}
      {remaining ? (
        <Chip size="small" label={`+${remaining} more`} sx={{ alignSelf: 'flex-start', height: 22, fontWeight: 900, color: accent, bgcolor: alpha(accent, 0.08) }} />
      ) : null}
    </Stack>
  );
};

const SocialPlatformChips = ({ employee }) => {
  const socialLinks = getSocialLinks(employee);
  const byPlatform = socialLinks.reduce((acc, item) => {
    if (!acc[item.platform]) acc[item.platform] = item;
    return acc;
  }, {});
  const platforms = ['linkedin', 'twitter', 'facebook', 'instagram'];

  return (
    <Stack direction="row" spacing={0.55} useFlexGap flexWrap="wrap">
      {platforms.map((platform) => {
        const item = byPlatform[platform];
        const { label, Icon, accent } = platformMeta[platform];
        const chipSx = {
          height: 28,
          fontWeight: 900,
          color: item ? accent : SLATE_TEXT,
          bgcolor: item ? alpha(accent, 0.09) : alpha(SLATE_TEXT, 0.08),
          border: `1px solid ${item ? alpha(accent, 0.16) : alpha(SLATE_TEXT, 0.12)}`,
          '& .MuiChip-icon': { color: item ? accent : SLATE_TEXT },
        };

        return item?.url ? (
          <Chip
            key={platform}
            component="a"
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            clickable
            size="small"
            icon={<Icon fontSize="small" />}
            label={label}
            sx={chipSx}
          />
        ) : (
          <Chip
            key={platform}
            size="small"
            icon={<Icon fontSize="small" />}
            label={item ? label : label.replace('/X', '')}
            sx={chipSx}
          />
        );
      })}
      {socialLinks.filter((item) => !platforms.includes(item.platform)).length ? (
        <Chip size="small" icon={<PublicIcon fontSize="small" />} label={`+${socialLinks.filter((item) => !platforms.includes(item.platform)).length}`} sx={{ height: 28, fontWeight: 900, color: '#7C3AED', bgcolor: alpha('#7C3AED', 0.08) }} />
      ) : null}
    </Stack>
  );
};

const LocationDisplay = ({ employee }) => {
  const location = getLocationParts(employee);
  return (
    <Stack direction="row" spacing={0.9} alignItems="flex-start">
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 2.2,
          bgcolor: location.isKnown ? alpha(BRAND_BLUE, 0.08) : alpha(SLATE_TEXT, 0.08),
          color: location.isKnown ? BRAND_BLUE : SLATE_TEXT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: location.flag ? '1.05rem' : '1rem',
        }}
      >
        {location.flag || <LocationOnIcon fontSize="small" />}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: location.isKnown ? DARK_TEXT : SLATE_TEXT, fontWeight: 850, lineHeight: 1.35, wordBreak: 'break-word' }}>
          {location.primary}
        </Typography>
        {location.secondary ? (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: SLATE_TEXT, lineHeight: 1.35, wordBreak: 'break-word' }}>
            {location.secondary}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
};

const EmployeeDrawer = ({ open, onClose, employee, detail, loadingDetail }) => {
  const selected = detail || employee;
  if (!selected) return null;

  const name = getName(selected);
  const socialLinks = getSocialLinks(selected);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 620 }, bgcolor: '#F7FAFE', borderLeft: `1px solid ${alpha(BRAND_BLUE, 0.1)}` } }}
    >
      <Box sx={{ p: 2.5, background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #003f8f 100%)`, color: '#ffffff' }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar sx={{ width: 56, height: 56, bgcolor: alpha('#ffffff', 0.18), color: '#ffffff', fontWeight: 900 }}>
            {getInitials(name)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.2 }}>{name}</Typography>
            <Typography sx={{ mt: 0.5, color: alpha('#ffffff', 0.88) }}>{pick(selected, ['title', 'job_title', 'role'], 'No title returned')}</Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.2 }}>
              {pick(selected, ['department'], '') ? <Chip size="small" label={pick(selected, ['department'], '')} sx={{ bgcolor: alpha('#ffffff', 0.16), color: '#ffffff', fontWeight: 800 }} /> : null}
              {pick(selected, ['seniority'], '') ? <Chip size="small" label={pick(selected, ['seniority'], '')} sx={{ bgcolor: alpha('#ffffff', 0.16), color: '#ffffff', fontWeight: 800 }} /> : null}
            </Stack>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#ffffff' }}><CloseIcon /></IconButton>
        </Stack>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {loadingDetail ? (
          <Typography sx={{ color: SLATE_TEXT }}>Loading employee details...</Typography>
        ) : null}

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <SectionCard eyebrow="Emails" title={`${getEmails(selected).length} discovered`}>
              <DataPills values={getEmails(selected)} accent={BRAND_BLUE} />
            </SectionCard>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SectionCard eyebrow="Phones" title={`${getPhones(selected).length} discovered`}>
              <DataPills values={getPhones(selected)} accent="#0F766E" />
            </SectionCard>
          </Grid>
        </Grid>

        <SectionCard eyebrow="Location" title="Public location context" sx={{ mb: 2 }}>
          <LocationDisplay employee={selected} />
        </SectionCard>

        <SectionCard eyebrow="Profile" title="Public profile context" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: SLATE_TEXT, lineHeight: 1.8 }}>
            {pick(selected, ['bio', 'summary', 'description'], 'No biography or profile summary was returned for this employee.')}
          </Typography>
          <Stack direction="row" spacing={0.85} useFlexGap flexWrap="wrap" sx={{ mt: 1.4 }}>
            {Object.entries(pick(selected, ['attributes'], {})).map(([key, value]) => hasValue(value) ? (
              <Chip key={key} label={`${titleCase(key)}: ${stringifyValue(value)}`} sx={{ fontWeight: 800, bgcolor: alpha(BRAND_BLUE, 0.07), color: BRAND_BLUE }} />
            ) : null)}
          </Stack>
        </SectionCard>

        <SectionCard eyebrow="Social Profiles" title="Public social references" sx={{ mb: 2 }}>
          {socialLinks.length ? (
            <Stack spacing={1}>
              {socialLinks.map(({ platform, url, value }) => {
                const { label, Icon, accent } = platformMeta[platform] || platformMeta.other;
                const profileName = pick(value?.profile, ['name', 'headline', 'title'], '');
                return (
                  <Box key={platform} sx={{ p: 1.5, borderRadius: 3, border: `1px solid ${alpha(BRAND_BLUE, 0.12)}`, bgcolor: '#ffffff' }}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(accent, 0.1), color: accent }}>
                          <Icon fontSize="small" />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 900, color: DARK_TEXT }}>{label}</Typography>
                          {profileName ? <Typography variant="body2" sx={{ color: SLATE_TEXT }}>{profileName}</Typography> : null}
                        </Box>
                      </Stack>
                      {url ? (
                        <Button
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          endIcon={<OpenInNewIcon />}
                          variant="outlined"
                          size="small"
                          sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 800, flexShrink: 0 }}
                        >
                          Open
                        </Button>
                      ) : null}
                    </Stack>
                    {url ? <Typography variant="caption" sx={{ mt: 0.7, display: 'block', color: SLATE_TEXT, wordBreak: 'break-word' }}>{url}</Typography> : null}
                    <SourceRefs refs={pick(value, ['source_refs', 'sourceRefs', 'sources'], [])} />
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <EmptyState title="No social profiles returned" description="Public profile links will appear here when discovered." />
          )}
        </SectionCard>

        <SectionCard eyebrow="Sources" title="Evidence references">
          <SourceRefs refs={pick(selected, ['source_refs', 'sourceRefs', 'sources'], [])} />
        </SectionCard>
      </Box>
    </Drawer>
  );
};

const EmployeesPage = () => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();
  const { loading, error, data } = useOsintFetch('/pentests/osint/employees', pentest_id, 'employees');
  const employees = useMemo(() => asArray(data).filter(hasValue), [data]);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('all');
  const [seniority, setSeniority] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetail, setEmployeeDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const departments = useMemo(() => Array.from(new Set(employees.map((employee) => pick(employee, ['department'], '')).filter(hasValue))).sort(), [employees]);
  const seniorities = useMemo(() => Array.from(new Set(employees.map((employee) => pick(employee, ['seniority'], '')).filter(hasValue))).sort(), [employees]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((employee) => {
      if (department !== 'all' && pick(employee, ['department'], '') !== department) return false;
      if (seniority !== 'all' && pick(employee, ['seniority'], '') !== seniority) return false;
      if (quickFilter === 'email' && !getEmails(employee).length) return false;
      if (quickFilter === 'phone' && !getPhones(employee).length) return false;
      if (quickFilter === 'linkedin' && !hasSocialPlatform(employee, 'linkedin')) return false;
      if (quickFilter === 'executive' && !isExecutive(employee)) return false;
      if (!q) return true;
      const haystack = [
        getName(employee),
        pick(employee, ['title', 'job_title', 'role'], ''),
        pick(employee, ['department'], ''),
        pick(employee, ['seniority'], ''),
        getLocation(employee),
        ...getEmails(employee),
        ...getPhones(employee),
        ...getSocialLinks(employee).map((item) => [item.platform, item.url].join(' ')),
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [department, employees, quickFilter, search, seniority]);

  const openEmployee = async (employee) => {
    setSelectedEmployee(employee);
    setEmployeeDetail(null);
    setDrawerOpen(true);
    const employeeId = getEmployeeId(employee);
    if (!employeeId) return;

    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    setLoadingDetail(true);
    try {
      const response = await fetch(`${BASEURL}/pentests/osint/employee?pentest_id=${pentest_id}&employee_id=${encodeURIComponent(employeeId)}`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const detailData = await response.json();
      if (!detailData?.error) {
        setEmployeeDetail(pick(detailData, ['employee', 'data', 'payload', 'result'], detailData));
      }
    } catch (fetchError) {
      console.error(fetchError);
    } finally {
      setLoadingDetail(false);
    }
  };

  if (loading) return <LoadingState label="Loading employee intelligence..." />;
  if (error) return <ErrorState message={error} />;

  const emailCount = employees.reduce((sum, employee) => sum + getEmails(employee).length, 0);
  const phoneCount = employees.reduce((sum, employee) => sum + getPhones(employee).length, 0);
  const socialCount = employees.reduce((sum, employee) => sum + getSocialLinks(employee).length, 0);
  const locationCount = employees.filter((employee) => getLocationParts(employee).isKnown).length;
  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const quickFilters = [
    { value: 'all', label: 'All People' },
    { value: 'email', label: 'Has Email' },
    { value: 'phone', label: 'Has Phone' },
    { value: 'linkedin', label: 'Has LinkedIn' },
    { value: 'executive', label: 'Executives' },
  ];

  return (
    <OsintShell>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} lg={3}><MetricCard label="Employees" value={formatNumber(employees.length)} helper="Public employee profiles returned by the OSINT agent." Icon={GroupsIcon} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><MetricCard label="Emails" value={formatNumber(emailCount)} helper="Email addresses found across employee records." Icon={EmailIcon} accent="#2563EB" /></Grid>
        <Grid item xs={12} sm={6} lg={3}><MetricCard label="Phones" value={formatNumber(phoneCount)} helper="Phone numbers tied to employee records." Icon={PhoneIcon} accent="#0F766E" /></Grid>
        <Grid item xs={12} sm={6} lg={3}><MetricCard label="Locations" value={formatNumber(locationCount)} helper="Employee records with public location context." Icon={LocationOnIcon} accent="#7C3AED" /></Grid>
      </Grid>

      <SectionCard eyebrow="Filters" title="Find the people that matter" sx={{ mb: 2.5 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(event) => { setSearch(event.target.value); setPage(0); }}
            placeholder="Search name, title, department, email, phone, or location..."
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ maxWidth: { lg: 540 } }}
          />
          <Select size="small" value={department} onChange={(event) => { setDepartment(event.target.value); setPage(0); }} sx={{ minWidth: 190 }}>
            <MenuItem value="all">All departments</MenuItem>
            {departments.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
          </Select>
          <Select size="small" value={seniority} onChange={(event) => { setSeniority(event.target.value); setPage(0); }} sx={{ minWidth: 180 }}>
            <MenuItem value="all">All seniority</MenuItem>
            {seniorities.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
          </Select>
        </Stack>
        <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
          {quickFilters.map((filter) => {
            const selected = quickFilter === filter.value;
            return (
              <Chip
                key={filter.value}
                label={filter.label}
                clickable
                onClick={() => { setQuickFilter(filter.value); setPage(0); }}
                sx={{
                  fontWeight: 900,
                  color: selected ? '#ffffff' : BRAND_BLUE,
                  bgcolor: selected ? BRAND_BLUE : alpha(BRAND_BLUE, 0.07),
                  border: `1px solid ${selected ? BRAND_BLUE : alpha(BRAND_BLUE, 0.13)}`,
                  '&:hover': { bgcolor: selected ? BRAND_BLUE : alpha(BRAND_BLUE, 0.12) },
                }}
              />
            );
          })}
          <Chip label={`${formatNumber(socialCount)} social refs`} icon={<PublicIcon fontSize="small" />} sx={{ fontWeight: 900, color: '#7C3AED', bgcolor: alpha('#7C3AED', 0.08), '& .MuiChip-icon': { color: '#7C3AED' } }} />
        </Stack>
      </SectionCard>

      <Card sx={{ ...shellCardSx, overflow: 'hidden' }}>
        <TableContainer>
          <Table sx={{ minWidth: 1180 }}>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: BRAND_BLUE, color: '#ffffff', fontWeight: 900, borderBottom: 'none', whiteSpace: 'nowrap' } }}>
                <TableCell>Employee</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Socials</TableCell>
                <TableCell>Location</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.length ? pageRows.map((employee) => (
                <TableRow
                  key={getEmployeeId(employee) || getName(employee)}
                  hover
                  sx={{
                    '&:nth-of-type(odd)': { bgcolor: alpha(BRAND_BLUE, 0.016) },
                    '&:hover': { bgcolor: `${alpha(BRAND_BLUE, 0.045)} !important` },
                    '& td': { borderBottomColor: alpha(BRAND_BLUE, 0.06), verticalAlign: 'top', py: 1.6 },
                  }}
                >
                  <TableCell sx={{ minWidth: 250, maxWidth: 300 }}>
                    <Stack direction="row" spacing={1.2} alignItems="flex-start">
                      <Avatar sx={{ bgcolor: alpha(BRAND_BLUE, 0.1), color: BRAND_BLUE, fontWeight: 900, border: `1px solid ${alpha(BRAND_BLUE, 0.16)}` }}>{getInitials(getName(employee))}</Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900, color: DARK_TEXT, lineHeight: 1.35, wordBreak: 'break-word' }}>{getName(employee)}</Typography>
                        <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap" sx={{ mt: 0.6 }}>
                          {pick(employee, ['department'], '') ? <Chip size="small" label={pick(employee, ['department'], '')} sx={{ height: 23, fontWeight: 850, color: BRAND_BLUE, bgcolor: alpha(BRAND_BLUE, 0.08) }} /> : null}
                          {isExecutive(employee) ? <Chip size="small" label="Executive" sx={{ height: 23, fontWeight: 850, color: '#7C3AED', bgcolor: alpha('#7C3AED', 0.08) }} /> : null}
                        </Stack>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ minWidth: 230, maxWidth: 300 }}>
                    <Typography sx={{ fontWeight: 850, color: DARK_TEXT, lineHeight: 1.4, wordBreak: 'break-word' }}>{pick(employee, ['title', 'job_title', 'role'], 'No title returned')}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.35, color: SLATE_TEXT }}>{pick(employee, ['seniority'], '') || (/founder|chief|ceo|cfo|coo|vp/i.test(String(pick(employee, ['job_title', 'title'], ''))) ? 'Executive' : 'No seniority returned')}</Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 220, maxWidth: 270 }}>
                    <ContactList values={getEmails(employee)} accent={BRAND_BLUE} emptyLabel="No email found" />
                  </TableCell>
                  <TableCell sx={{ minWidth: 190, maxWidth: 240 }}>
                    <ContactList values={getPhones(employee)} accent="#0F766E" emptyLabel="No phone found" />
                  </TableCell>
                  <TableCell sx={{ minWidth: 260 }}>
                    <SocialPlatformChips employee={employee} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 220, maxWidth: 270 }}>
                    <LocationDisplay employee={employee} />
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 120 }}>
                    <Button variant="outlined" size="small" startIcon={<PersonIcon />} onClick={() => openEmployee(employee)} sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 2.5, whiteSpace: 'nowrap' }}>
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={7}>
                    <EmptyState title="No employees match the current filters" description="Try clearing search, department, or seniority filters." />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      <EmployeeDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        employee={selectedEmployee}
        detail={employeeDetail}
        loadingDetail={loadingDetail}
      />
    </OsintShell>
  );
};

export default EmployeesPage;
